<template>
    <div>
        <!-- Mobile Sidebar (Controlled by showMobile Prop) -->
        <div v-if="showMobile">
            <button
                @click="toggleSidebar"
                class="flex items-center justify-center p-2 text-white transition-all bg-blue-500 rounded-md shadow-md hover:bg-blue-600"
            >
                <!-- AppIcon with transition and toggle effect -->
                <AppIcon
                    :icon="toggleIcon"
                    class="w-6 h-6 transition-transform duration-300 transform"
                    :class="
                        isSidebarOpen
                            ? 'rotate-90 scale-110'
                            : 'rotate-0 scale-100'
                    "
                />
            </button>

            <!-- Sidebar Overlay (Mobile) -->
            <div
                :class="[
                    'fixed inset-0 z-40 transition-transform',
                    isSidebarOpen ? 'translate-x-0' : '-translate-x-full',
                ]"
                class="w-64 h-full pt-8 bg-white shadow-lg"
            >
                <!-- <button @click="toggleSidebar" class="absolute text-gray-600 top-4 right-4 hover:text-blue-500">
        <AppIcon icon="mdi-close" class="w-6 h-6" />
        </button> -->
                <div class="flex items-center gap-4 px-8">
                    <div class="text-gray-600">
                        <router-link to="/" class="text-xl font-semibold"
                            >Logo</router-link
                        >
                    </div>
                </div>

                <!-- Sidebar Links -->
                <div class="flex flex-col p-4 mt-10 space-y-4">
                    <router-link
                        v-for="(route, index) in dashboardRoutes"
                        :key="index"
                        :to="{ name: route.name }"
                        class="flex items-center p-3 space-x-2 font-medium transition rounded-lg"
                        :class="isActive(route)"
                        @click="closeSidebarOnMobile"
                    >
                        <AppIcon :icon="route.icon" class="w-5 h-5" />
                        <span>{{ $t(route.routeLabel) }}</span>
                    </router-link>

                    <button
                        v-if="!isGuest"
                        @click="logout"
                        class="flex items-center p-3 space-x-2 font-medium text-gray-600 transition rounded-lg hover:bg-gray-100"
                    >
                        <AppIcon
                            icon="mdi-logout"
                            class="w-5 h-5 text-red-500"
                        />
                        <span class="text-red-500">{{ $t("Logout") }}</span>
                    </button>
                </div>
            </div>

            <!-- Sidebar Background Overlay -->
            <div
                v-if="isSidebarOpen"
                @click="toggleSidebar"
                class="fixed inset-0 z-30 bg-black bg-opacity-50"
            ></div>
        </div>

        <!-- Desktop Sidebar (Default Behavior) -->
        <div v-else>
            <div
                :class="[
                    'fixed inset-0 z-40 transition-transform lg:static lg:z-auto lg:translate-x-0',
                    isSidebarOpen ? 'translate-x-0' : '-translate-x-full',
                ]"
                class="w-64 h-full pt-16 bg-white shadow-lg lg:shadow-none"
            >
                <div class="flex flex-col p-4 mt-10 space-y-4 lg:mt-0">
                    <router-link
                        v-for="(route, index) in dashboardRoutes"
                        :key="index"
                        :to="`/dashboard/${route.path}`"
                        class="flex items-center p-3 space-x-2 font-medium transition rounded-lg"
                        :class="
                            isActive(route.path)
                                ? 'bg-blue-500 text-white'
                                : 'text-gray-600 hover:bg-gray-100'
                        "
                        @click="closeSidebarOnMobile"
                    >
                        <AppIcon :icon="route.icon" class="w-5 h-5" />
                        <span>{{ $t(route.routeLabel) }}</span>
                    </router-link>

                    <button
                        v-if="!isGuest"
                        @click="logout"
                        class="flex items-center p-3 space-x-2 font-medium text-gray-600 transition rounded-lg hover:bg-gray-100"
                    >
                        <AppIcon
                            icon="mdi-logout"
                            class="w-5 h-5 text-red-500"
                        />
                        <span class="text-red-500">{{ $t("Logout") }}</span>
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { useRoute } from "vue-router";
import { mapActions, mapState } from "vuex";

export default {
    name: "AppSidebar",
    props: {
        showMobile: {
            type: Boolean,
            default: false, // Sidebar mobile tidak ditampilkan secara default
        },
    },
    data() {
        return {
            isSidebarOpen: false, // State untuk mengontrol sidebar visibility
        };
    },
    computed: {
        ...mapState("user", {
            isGuest: (state) => state.isGuest, // Ambil isGuest dari Vuex
        }),

        isDesktop() {
            return window.innerWidth >= 1024; // Deteksi desktop menggunakan lebar layar
        },
        toggleIcon() {
            // Ganti ikon berdasarkan status isSidebarOpen
            return this.isSidebarOpen ? "mdi:close" : "mdi:menu";
        },
        dashboardRoutes() {
            const isGuest = this.$store.getters["user/isGuest"];

            // Cari route root (path "/")
            const root = this.$router.options.routes.find(
                (r) => r.path === "/"
            );
            // Cari /dashboard di dalam root.children
            const dashboard = root?.children?.find(
                (r) => r.path === "/dashboard" || r.name === "Dashboard"
            );
            const children = dashboard?.children || [];

            // Filter: sembunyikan Profile kalau guest, dan hanya yang menu === true
            return children.filter((route) => {
                if (isGuest && route.name === "Profile") return false;
                return route.menu; // pakai flag "menu" milikmu
            });
        },
    },
    methods: {
        ...mapActions("user", ["logout", "clearError"]),

        toggleSidebar() {
            this.isSidebarOpen = !this.isSidebarOpen;
        },
        closeSidebarOnMobile() {
            if (!this.isDesktop) {
                this.isSidebarOpen = false; // Tutup sidebar jika di mobile
            }
        },
        isActive(routePath) {
            const currentRoute = useRoute();
            return currentRoute.path.includes(routePath);
        },
        formatRouteName(path) {
            return path
                .replace(/-/g, " ")
                .replace(/^\w/, (c) => c.toUpperCase());
        },
    },
};
</script>

<style scoped>
/* Posisi dan transisi sidebar */
.transition-transform {
    transition: transform 0.3s ease-in-out;
}

/* Tambahkan padding atas untuk sidebar agar tidak tertutup header */
.pt-16 {
    padding-top: 4rem;
    /* Sesuaikan dengan tinggi header */
}

/* Warna dan efek tombol */
.bg-blue-500 {
    background-color: #3b82f6;
}

.bg-blue-600:hover {
    background-color: #2563eb;
}

.text-white {
    color: white;
}
</style>
